import React      from 'react';
import classNames from 'classnames';

import { random } from '../../../utils';
import styles     from './ProgressiveImage.css';

type ImgProps = {
    vertical?: boolean;
    [key: string]: any;
};

type ProgressiveImageProps = {
    title?: string;
    alt?: string;
    useParentSize?: boolean;
    loadWhenViewable?: boolean;
    image?: string;
    className?: string;
    vertical?: boolean;
};

type ProgressiveImageState = {
    color: string;
    image?: Element;
    loading: boolean;
};

const ProgressiveImageBase = ({ vertical, ...props }: ImgProps) => (
    <img
        className = {classNames(styles.progressiveImageBase, {
            [styles.vertical]: !!vertical}
        )}

        {...props}
    />
);

const ColorBlock = ({ color, ...props }: any) => (
    <div
        className = {styles.colorBlock}
        style = {{backgroundColor: color}}
        {...props}
    />
);

const ProgressiveContainer = React.forwardRef<any, any>(({className, ...props}: any, ref) => (
    <div className = {classNames(styles.progressiveContainer, className)} {...props} ref = {ref} />
));

export class ProgressiveImage extends React.Component<ProgressiveImageProps, ProgressiveImageState> {
    static COLORS = [
        '#ef6c57', '#7ed3b2', '#b9e6d3', '#cd4545',
        '#ebf0c2', '#cde8f6', '#34a7b2', '#d195f9',
        '#e4406f', '#8fbbaf', '#6b7b8e', '#6b7b8e',
        '#fff0bc', '#db996c'];

    static displayName = 'ProgressiveImage';

    static getDerivedStateFromProps(props, state) {
        if (!state.image || (state.image !== props.image)) {
            return {
                image: null,
                loading: true,
            };
        }

        return state;
    }

    containerRef = React.createRef<HTMLDivElement>();
    loadingImage: any;

    state = {
        color: ProgressiveImage.COLORS[random(0, ProgressiveImage.COLORS.length - 1 )],
        image: null,
        loading: true,
    };

    isViewable() {
        const element: Element = this.containerRef.current as Element;

        if (!element) { return false; }

        let win = window;
        const bounds = this.props.useParentSize
            ? element.parentElement.getBoundingClientRect() : element.getBoundingClientRect();

        if (window.parent.window !== window && window.frameElement) {
            win = window.parent.window;
        }

        const preloadOffset = 100;

        return bounds.top <= win.innerHeight + preloadOffset;
    }

    onScroll = () => {
        if (this.isViewable()) {
            window.removeEventListener('scroll', this.onScroll);
            this.fetchImage(this.props.image);
        }
    }

    componentDidMount() {
        this.tryLoad();
    }

    componentDidUpdate(prevProps: ProgressiveImageProps) {
        if (prevProps.image !== this.props.image) {
            this.tryLoad();
        }
    }

    componentWillUnmount() {
        if (this.loadingImage) {
            this.loadingImage.onload = null;
        }
        window.removeEventListener('scroll', this.onScroll);
    }

    tryLoad() {
        if (this.props.loadWhenViewable) {
            this.onScroll();
            window.addEventListener('scroll', this.onScroll);
        } else {
            this.fetchImage(this.props.image);
        }
    }

    fetchImage = (src: string) => {
        const image = new Image();
        image.onload = () => {
            this.setState({image: this.props.image as unknown as Element, loading: false});
        };
        image.src = src;
        this.loadingImage = image;
    }

    render() {
        const { image, loading, color } = this.state;
        const { title, alt, className, vertical } = this.props;
        let img = null;

        if (loading) {
            img = <ColorBlock key = "loading" color = {color} />;
        } else {
            /**
             * TODO Implement lighthouse advise
             * <source src> with a <picture> parent is invalid and therefore
              ignored. Please use <source srcset> instead.
            */

            img = (
                <picture key = "img-loaded" className = {classNames({ [styles.verticalPicture]: !!vertical })}>
                    <source src={image} srcSet={image} type="image/jpg"/>
                    <ProgressiveImageBase
                        vertical = {vertical}
                        aria-label = "Game Image"
                        role = "img"
                        src = {image}
                        srcSet = {image}
                        title = {title}
                        alt = {alt || 'Game Image'}
                    />
                </picture>
            );
        }

        return (
            <ProgressiveContainer
                key = "img"
                ref = {this.containerRef}
                className = {className}
            >
                {img}
            </ProgressiveContainer>
        );
    }
}
